/**
 * AI-21 (#190, #191) — assistentens hukommelse: kontraktene for
 * `/api/ai/memories` og årsakskodene fra memory-guard.
 *
 * Én kilde for apps/api (ruter og memory.service) og apps/web (innstillingene
 * «Hva Stell husker», #193). Spesifikasjon: docs/chat-agent/memory-plan.md, 1e.
 */
import { z } from 'zod';

/** Loose UUID regex — accepts both v4 and v7 (36-char canonical form). */
const UuidSchema = z.string().regex(/^[0-9a-f-]{36}$/i, 'invalid_uuid');

/** Lengste notattekst etter normalisering (trim + sammenslått mellomrom). */
export const MEMORY_TEXT_MAX = 200;
/** Tak på aktiv notattekst per familie, i tegn — hele lista skal få plass i prompten. */
export const MEMORY_FAMILY_BUDGET = 3000;
/** Tak på aktiv notattekst om ett medlem, i tegn. */
export const MEMORY_MEMBER_BUDGET = 1000;
/** Tak på ventende notater per forfatter, så en tenåring ikke kan fylle godkjenningskøen (review-runde 1). */
export const MEMORY_PENDING_PER_AUTHOR = 10;

export const MemoryAudienceSchema = z.enum(['all', 'parents']);
export type MemoryAudience = z.infer<typeof MemoryAudienceSchema>;

export const MemoryKindSchema = z.enum(['fact', 'preference', 'routine', 'style']);
export type MemoryKind = z.infer<typeof MemoryKindSchema>;

export const MemoryStatusSchema = z.enum(['active', 'pending', 'rejected']);
export type MemoryStatus = z.infer<typeof MemoryStatusSchema>;

export const MemorySourceSchema = z.enum(['chat', 'manual', 'suggested']);
export type MemorySource = z.infer<typeof MemorySourceSchema>;

/**
 * Hvorfor memory-guard avviste en tekst. Verktøyet (#192) gjør koden om til
 * en kort norsk melding til modellen; UI-et (#193) til en i18n-tekst.
 *   - `allergen` / `diet`: hører hjemme i medlemmets kostholdsinnstillinger (Pattern 6)
 *   - `pii`: fødselsnummer, e-post, telefonnummer eller gateadresse
 *   - `script`: bokstaver utenfor latinsk skrift — fanger kyrilliske og andre
 *     lookalikes («еɡɡ») som ellers ville sneket et allergen forbi leksikonet
 *   - `health`: diagnoser og behandling (diabetes, astma, insulin …), #210
 */
export const MemoryGuardReasonSchema = z.enum(['empty', 'too_long', 'script', 'allergen', 'diet', 'pii', 'health']);
export type MemoryGuardReason = z.infer<typeof MemoryGuardReasonSchema>;

/** Et notat slik API-et gir det ut. */
export const MemoryDTOSchema = z.object({
  id: UuidSchema,
  ref: z.number().int().positive(),
  /** Hvem notatet handler om; null = hele familien. */
  member_id: UuidSchema.nullable(),
  audience: MemoryAudienceSchema,
  kind: MemoryKindSchema,
  text: z.string(),
  status: MemoryStatusSchema,
  source: MemorySourceSchema,
  created_by: UuidSchema.nullable(),
  expires_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  /**
   * #232: hvorfor dagens memory-guard ville avvist den lagrede teksten, eller
   * `null` når den går gjennom. Beregnet ved hver lesing (ingen migrasjon), så
   * et notat lagret før vakten ble strengere — eller før husholdningen fikk en
   * ny allergi — blir synlig for en forelder. Bare i `GET /api/ai/memories` for
   * admin (`memories` og `pending`); ellers utelatt. Et flagget notat holdes
   * utenfor promptblokken til det er endret.
   */
  guard_reason: MemoryGuardReasonSchema.nullable().optional(),
});
export type MemoryDTO = z.infer<typeof MemoryDTOSchema>;

/** Notatene om ett arkivert medlem (#227): skjult fra prompten og `memories`, men beholdt. */
export const ArchivedMemberMemoriesSchema = z.object({
  member_id: UuidSchema,
  /** Medlemmets navn — `/api/members/basic` gir ikke arkiverte medlemmer. */
  name: z.string(),
  memories: z.array(MemoryDTOSchema),
});
export type ArchivedMemberMemories = z.infer<typeof ArchivedMemberMemoriesSchema>;

/** `GET /api/ai/memories`: det medlemmet kan se, `pending` (bare admin), `own_pending` (bare tenåring/barn) og `archived` (bare admin). */
export const MemoryListResponseSchema = z.object({
  /** `families.ai_memory_enabled` — alle medlemmer får vite om hukommelsen er på. */
  enabled: z.boolean(),
  memories: z.array(MemoryDTOSchema),
  /** Godkjenningskøen: alle ventende notater i familien. Tom for alle andre enn admin. */
  pending: z.array(MemoryDTOSchema),
  /**
   * Innloggedes egne ventende notater, så en tenåring eller et barn kan se og
   * trekke tilbake det de har foreslått (#211). Aldri andres; tom for admin,
   * som ser alle i `pending`.
   */
  own_pending: z.array(MemoryDTOSchema),
  /**
   * Aktive notater om arkiverte medlemmer, per medlem (#227). De er ute av
   * prompten og av `memories`, og vises for foreldrene i en sammenfoldet
   * «Arkivert»-gruppe der de kan slettes. Tom for alle andre enn admin.
   */
  archived: z.array(ArchivedMemberMemoriesSchema),
});
export type MemoryListResponse = z.infer<typeof MemoryListResponseSchema>;

/**
 * Råteksten fra klienten. Taket her er bare et vern mot store kropper;
 * memory-guard normaliserer og håndhever `MEMORY_TEXT_MAX`, så svaret blir
 * årsakskoden `too_long` og ikke en generisk valideringsfeil.
 */
const MemoryTextInput = z.string().max(1000);
const ExpiresAtInput = z.string().datetime({ offset: true }).nullable();

/** `POST /api/ai/memories` — notat lagt inn for hånd (`source: 'manual'`). */
export const CreateMemoryRequestSchema = z.object({
  text: MemoryTextInput,
  /** Hvem notatet handler om; null = hele familien. */
  member_id: UuidSchema.nullable(),
  audience: MemoryAudienceSchema.default('all'),
  kind: MemoryKindSchema.default('fact'),
  expires_at: ExpiresAtInput.optional(),
});
export type CreateMemoryRequest = z.infer<typeof CreateMemoryRequestSchema>;

/** `PATCH /api/ai/memories/:id` — tekst, målgruppe, subjekt, type eller utløp. */
export const UpdateMemoryRequestSchema = z
  .object({
    text: MemoryTextInput,
    member_id: UuidSchema.nullable(),
    audience: MemoryAudienceSchema,
    kind: MemoryKindSchema,
    expires_at: ExpiresAtInput,
  })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'empty_patch' });
export type UpdateMemoryRequest = z.infer<typeof UpdateMemoryRequestSchema>;

/**
 * Svaret fra `DELETE /api/ai/memories/:id` og `POST /api/ai/memories/:id/withdraw`
 * (#218): bare id og nummer, aldri teksten — en sletting skal ikke bli en vei
 * til å lese et notat (review-runde 1 i !172).
 */
export const MemoryDeletedResponseSchema = z.object({
  ok: z.literal(true),
  id: UuidSchema,
  ref: z.number().int().positive(),
});
export type MemoryDeletedResponse = z.infer<typeof MemoryDeletedResponseSchema>;

/** `GET/PUT /api/ai/memories/settings` — familiens bryter (`families.ai_memory_enabled`). */
export const MemorySettingsSchema = z.object({ enabled: z.boolean() });
export type MemorySettings = z.infer<typeof MemorySettingsSchema>;
